import AppButton from '@nxsys/components/AppButton';
import AppCheckbox from '@nxsys/components/AppCheckbox';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { Icons } from '@nxsys/types/icons';
import { countryList } from '@nxsys/utils';
import {
  formatPostCode,
  postCodeValidationRule,
} from '@nxsys/utils/formFieldValidation';
import { Col, Form, Row } from 'antd';
import { FormProps } from 'antd/lib';
import { Ref, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';
import StepTitle from './StepTitle';

export type DepartmentValues = {
  departments: Array<{
    department: string;
    isSameAsCompany?: boolean;
    companyName?: string;
    address1?: string;
    address2?: string;
    address3?: string;
    address4?: string;
    city?: string;
    county?: string;
    postCode?: string;
    country?: { label: string; value: string };
    email?: string;
  }>;
};

export type DepartmentFormRef = {
  isValidForm: () => Promise<boolean>;
  getValues: () => DepartmentValues;
  resetFields: () => void;
};

type Props = {
  formRef: Ref<DepartmentFormRef>;
  initialValues: Partial<DepartmentValues>;
  stepNumber: number;
};

const Department: React.FC<Props> = ({
  formRef,
  initialValues,
  stepNumber,
}) => {
  const { messages } = useTypedIntl();
  const { payrollCountry, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;

  const [form] = Form.useForm<DepartmentValues>();
  const watchDepartments = Form.useWatch('departments', form);

  useImperativeHandle(formRef, () => ({
    isValidForm: async () => {
      try {
        await form.validateFields();
        return true;
      } catch {
        return false;
      }
    },
    getValues() {
      return form.getFieldsValue(true);
    },
    resetFields() {
      form.resetFields();
    },
  }));

  const onFinish: FormProps['onFinish'] = (values) => {
    console.info('values', values);
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <div className="Department">
      <StepTitle
        step={stepNumber}
        title="Department"
        description={
          isBureau
            ? 'Set up departments for your employer'
            : 'Set up departments for your company'
        }
      />

      <div className="mt-[24px]">
        <AppFormContainer
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          initialValues={{
            ...initialValues,
          }}
          layout="vertical"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          <Form.List name="departments">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => {
                  const isSameAsCompany =
                    watchDepartments?.[index]?.isSameAsCompany ?? true;

                  return (
                    <div
                      key={field.key}
                      className="border border-neutral-200 rounded-[8px] p-[16px] mb-[12px]"
                    >
                      <div className="flex items-start gap-x-[12px]">
                        <div className="flex-1">
                          <Form.Item
                            label="Department Name"
                            name={[field.name, 'department']}
                            rules={[
                              {
                                required: true,
                                message: 'Please enter department name',
                              },
                              {
                                validator: (_, value) => {
                                  if (!value) return Promise.resolve();
                                  const isDuplicate = (watchDepartments ?? [])
                                    .slice(0, index)
                                    .some((d) => d?.department === value);
                                  return isDuplicate
                                    ? Promise.reject(
                                        new Error('Department already exists!'),
                                      )
                                    : Promise.resolve();
                                },
                              },
                            ]}
                          >
                            <AppInput placeholder="Department Name" />
                          </Form.Item>

                          {isIreland && (
                            <>
                              <Form.Item
                                name={[field.name, 'isSameAsCompany']}
                                valuePropName="checked"
                                className="mb-[8px]"
                              >
                                <AppCheckbox>
                                  Invoicing Details Same as Employer Details?
                                </AppCheckbox>
                              </Form.Item>

                              {!isSameAsCompany && (
                                <div className="mt-[12px]">
                                  <Row gutter={[16, 0]}>
                                    <Col span={12}>
                                      <Form.Item
                                        label="Employer Name"
                                        name={[field.name, 'companyName']}
                                      >
                                        <AppInput placeholder="Employer Name" />
                                      </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                      <Form.Item
                                        label="Address 1"
                                        name={[field.name, 'address1']}
                                      >
                                        <AppInput placeholder="Address 1" />
                                      </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                      <Form.Item
                                        label="Address 2"
                                        name={[field.name, 'address2']}
                                      >
                                        <AppInput placeholder="Address 2" />
                                      </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                      <Form.Item
                                        label="Address 3"
                                        name={[field.name, 'address3']}
                                      >
                                        <AppInput placeholder="Address 3" />
                                      </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                      <Form.Item
                                        label="Address 4"
                                        name={[field.name, 'address4']}
                                      >
                                        <AppInput placeholder="Address 4" />
                                      </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                      <Form.Item
                                        label="City/Town"
                                        name={[field.name, 'city']}
                                      >
                                        <AppInput placeholder="City/Town" />
                                      </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                      <Form.Item
                                        label="County"
                                        name={[field.name, 'county']}
                                      >
                                        <AppInput placeholder="Enter county" />
                                      </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                      <Form.Item
                                        label={messages['common.eircode']}
                                        name={[field.name, 'postCode']}
                                        rules={[
                                          postCodeValidationRule(
                                            messages,
                                            payrollCountry,
                                          ),
                                        ]}
                                      >
                                        <AppInput
                                          placeholder={
                                            messages['common.eircode']
                                          }
                                          upperCaseAll
                                          onChange={(e) => {
                                            const formatted = formatPostCode(
                                              e.target.value,
                                              payrollCountry,
                                            );
                                            form.setFields([
                                              {
                                                name: [
                                                  'departments',
                                                  field.name,
                                                  'postCode',
                                                ],
                                                value: formatted,
                                              },
                                            ]);
                                          }}
                                          maxLength={8}
                                        />
                                      </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                      <Form.Item
                                        label="Country"
                                        name={[field.name, 'country']}
                                      >
                                        <AppSelect
                                          options={countryList}
                                          placeholder="Country"
                                        />
                                      </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                      <Form.Item
                                        label="Email"
                                        name={[field.name, 'email']}
                                        rules={[
                                          {
                                            type: 'email',
                                            message:
                                              'Please enter a valid email',
                                          },
                                        ]}
                                      >
                                        <AppInput
                                          placeholder="Email"
                                          allowCapitalize={false}
                                        />
                                      </Form.Item>
                                    </Col>
                                  </Row>
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        <div
                          className="cursor-pointer"
                          onClick={() => remove(field.name)}
                        >
                          <Icons.IconTrash />
                        </div>
                      </div>
                    </div>
                  );
                })}

                <AppButton
                  variant="outlined"
                  leftIcon={<Icons.IconPlus />}
                  name="Add new Department"
                  onClick={() => add({ department: '', isSameAsCompany: true })}
                />
              </>
            )}
          </Form.List>
        </AppFormContainer>
      </div>
    </div>
  );
};

export default Department;
